import {
    Avatar,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Switch,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import ArchiveIcon from "@mui/icons-material/Archive";
import UploadIcon from "@mui/icons-material/Upload";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import UnpublishedIcon from "@mui/icons-material/Unpublished";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import AxiosInstance from "../../Config/AxiosInstance";
import { ApiList } from "../../Config/ApiList";
import TableContainer from "../../Components/TableContainer";

// Badge Manager — Trophy Case catalog CRUD.
//
// Criteria TYPES are code (backend evaluator registry); this screen composes
// badges from them. The list endpoint returns `criteria_types` (a map of
// type -> param schema), which drives the dynamic parameter fields in the
// form, so new criteria types need no admin-frontend change.

const EMPTY_FORM = {
    id: null,
    code: "",
    name: "",
    description: "",
    category: "milestone",
    criteria_type: "activity_count",
    criteria_params: {},
    is_premium: true,
    xp_reward: 0,
    sort_order: 0,
    available_from: "",
    available_until: "",
};

const STATUS_META = {
    1: { label: "live", bg: "#c6ff0060" },
    0: { label: "draft", bg: "#8884" },
    "-1": { label: "archived", bg: "#ff444430" },
};

// Input kind per paramSchema type (mirrors service/badge_evaluator.js
// PARAM_TYPES; values still validated server-side).
const PARAM_INPUTS = {
    activity_type_or_any: {
        kind: "select",
        // "tracked" = the GPS family (walk|hike|run) — the right default for
        // walk-style medals. "any" also counts timed sessions (play, fetch,
        // training, swim), which would let a Play session advance "100 Walks".
        options: ["tracked", "any", "walk", "hike", "run", "play", "fetch", "training", "swim"],
    },
    comparator: { kind: "select", options: ["gte", "lte"] },
    positive_int: { kind: "number" },
    nonnegative_int: { kind: "number" },
    positive_number: { kind: "number" },
    mmdd: { kind: "text", placeholder: "MM-DD (e.g. 10-31)" },
    hhmm: { kind: "text", placeholder: "HH:MM 24h (e.g. 08:00)" },
    date: { kind: "text", placeholder: "YYYY-MM-DD" },
    bool: { kind: "bool" },
};

const BadgeManager = () => {
    const [loading, setLoading] = useState(true);
    const [badges, setBadges] = useState([]);
    const [criteriaTypes, setCriteriaTypes] = useState({});
    const [categories, setCategories] = useState([]);
    const [page, setPage] = useState(0);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [uploadingId, setUploadingId] = useState(null);
    const fileInputRef = useRef(null);
    const uploadTargetRef = useRef(null);
    const pageSize = 25;

    const fetchBadges = useCallback(async () => {
        setLoading(true);
        try {
            const res = await AxiosInstance.get(ApiList.ADMIN_BADGES);
            if (res?.data?.s === 1) {
                const rows = (res.data.r.badges || []).map((b, i) => ({ ...b, rowid: i + 1 }));
                setBadges(rows);
                setCriteriaTypes(res.data.r.criteria_types || {});
                setCategories(res.data.r.categories || []);
            } else {
                toast.error(res?.data?.m || "Failed to load badges");
            }
        } catch {
            toast.error("Failed to load badges");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchBadges();
    }, [fetchBadges]);

    // ── create / edit ────────────────────────────────────────────────────
    const openAdd = () => {
        setForm(EMPTY_FORM);
        setDialogOpen(true);
    };

    const openEdit = (b) => {
        let params = b.criteria_params;
        if (typeof params === "string") {
            try { params = JSON.parse(params); } catch { params = {}; }
        }
        setForm({
            id: b.id,
            code: b.code,
            name: b.name,
            description: b.description || "",
            category: b.category,
            criteria_type: b.criteria_type,
            criteria_params: params || {},
            is_premium: !!b.is_premium,
            xp_reward: b.xp_reward || 0,
            sort_order: b.sort_order || 0,
            available_from: b.available_from ? String(b.available_from).slice(0, 10) : "",
            available_until: b.available_until ? String(b.available_until).slice(0, 10) : "",
        });
        setDialogOpen(true);
    };

    const saveBadge = async () => {
        if (saving) return;
        const code = form.code.trim().toLowerCase();
        if (!/^[a-z0-9_]{2,64}$/.test(code)) {
            toast.error("Code must be 2-64 chars of a-z, 0-9, _");
            return;
        }
        if (form.name.trim().length < 2) {
            toast.error("Name required");
            return;
        }
        setSaving(true);
        try {
            const body = {
                code,
                name: form.name.trim(),
                description: form.description.trim(),
                category: form.category,
                criteria_type: form.criteria_type,
                criteria_params: cleanParams(form.criteria_params),
                is_premium: form.is_premium ? 1 : 0,
                xp_reward: Number(form.xp_reward) || 0,
                sort_order: Number(form.sort_order) || 0,
                available_from: form.available_from ? `${form.available_from} 00:00:00` : null,
                available_until: form.available_until ? `${form.available_until} 23:59:59` : null,
            };
            const res = form.id
                ? await AxiosInstance.post(`${ApiList.ADMIN_BADGES}/${form.id}`, body)
                : await AxiosInstance.post(ApiList.ADMIN_BADGES, body);
            if (res?.data?.s === 1) {
                toast.success(form.id ? "Badge updated" : "Badge created as draft");
                setDialogOpen(false);
                fetchBadges();
            } else {
                toast.error(res?.data?.m || "Save failed");
            }
        } catch {
            toast.error("Save failed");
        } finally {
            setSaving(false);
        }
    };

    // Numbers arrive as strings from inputs — coerce so the backend's
    // paramSchema validation sees real numbers.
    const cleanParams = (params) => {
        const out = {};
        for (const [k, v] of Object.entries(params || {})) {
            if (v === "" || v === null || v === undefined) continue;
            out[k] = typeof v === "string" && v !== "" && !isNaN(Number(v)) && !/[-:]/.test(v)
                ? Number(v)
                : v;
        }
        return out;
    };

    // ── art upload ───────────────────────────────────────────────────────
    const pickArt = (badge) => {
        uploadTargetRef.current = badge;
        fileInputRef.current?.click();
    };

    const onArtSelected = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = ""; // allow re-picking the same file
        const badge = uploadTargetRef.current;
        if (!file || !badge) return;
        if (file.type !== "image/png") {
            toast.error("Badge art must be a PNG");
            return;
        }
        setUploadingId(badge.id);
        try {
            const fd = new FormData();
            fd.append("image", file);
            const res = await AxiosInstance.post(`${ApiList.ADMIN_BADGES}/${badge.id}/image`, fd);
            if (res?.data?.s === 1) {
                toast.success("Art uploaded");
                fetchBadges();
            } else {
                toast.error(res?.data?.m || "Upload failed");
            }
        } catch {
            toast.error("Upload failed");
        } finally {
            setUploadingId(null);
        }
    };

    // ── status transitions ───────────────────────────────────────────────
    const setStatus = async (badge, status) => {
        if (status === 1) {
            const result = await Swal.fire({
                title: `Publish "${badge.name}"?`,
                text: "The badge goes live in the app immediately and becomes earnable.",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#b9cc03",
                confirmButtonText: "Go live",
            });
            if (!result.isConfirmed) return;
        }
        if (status === -1) {
            const result = await Swal.fire({
                title: `Archive "${badge.name}"?`,
                text: "Already-earned badges stay in every trophy case forever; the badge just stops being earnable.",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#d33",
                confirmButtonText: "Archive",
            });
            if (!result.isConfirmed) return;
        }
        try {
            const res = await AxiosInstance.post(`${ApiList.ADMIN_BADGES}/${badge.id}/status`, { status });
            if (res?.data?.s === 1) {
                toast.success(res.data.m);
                fetchBadges();
            } else {
                toast.error(res?.data?.m || "Status change failed");
            }
        } catch {
            toast.error("Status change failed");
        }
    };

    // ── dynamic criteria param fields ────────────────────────────────────
    const schema = criteriaTypes[form.criteria_type] || {};
    const paramField = (key, spec) => {
        const input = PARAM_INPUTS[spec.type] || { kind: "text" };
        const value = form.criteria_params[key] ?? "";
        const setParam = (v) =>
            setForm({ ...form, criteria_params: { ...form.criteria_params, [key]: v } });
        const label = `${key}${spec.required ? " *" : ""}`;
        if (input.kind === "select") {
            return (
                <FormControl fullWidth margin="normal" key={key}>
                    <InputLabel>{label}</InputLabel>
                    <Select value={value} label={label} onChange={(e) => setParam(e.target.value)}>
                        {input.options.map((o) => (
                            <MenuItem key={o} value={o}>{o}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            );
        }
        if (input.kind === "bool") {
            return (
                <FormControlLabel
                    key={key}
                    control={<Checkbox checked={!!value} onChange={(e) => setParam(e.target.checked)} />}
                    label={label}
                />
            );
        }
        return (
            <TextField
                key={key}
                fullWidth
                margin="normal"
                type={input.kind === "number" ? "number" : "text"}
                label={label}
                placeholder={input.placeholder}
                value={value}
                onChange={(e) => setParam(e.target.value)}
            />
        );
    };

    // ── table ────────────────────────────────────────────────────────────
    const columns = useMemo(() => [
        { field: "rowid", headerName: "#", width: 55, sortable: false },
        {
            field: "image",
            headerName: "Art",
            width: 70,
            sortable: false,
            renderCell: (p) =>
                p.value ? (
                    <Avatar src={`${ApiList.IMG_URL}${p.value}`} variant="rounded" sx={{ width: 40, height: 40 }} />
                ) : (
                    <Avatar variant="rounded" sx={{ width: 40, height: 40, bgcolor: "#8883" }}>
                        <EmojiEventsIcon sx={{ color: "#666" }} />
                    </Avatar>
                ),
        },
        { field: "name", headerName: "Name", flex: 1, minWidth: 150 },
        {
            field: "code",
            headerName: "Code",
            flex: 1,
            minWidth: 140,
            renderCell: (p) => (
                <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: "#000" }}>
                    {p.value}
                </Typography>
            ),
        },
        {
            field: "category",
            headerName: "Category",
            width: 110,
            renderCell: (p) => (
                <Chip size="small" label={p.value.replace("_", " ")} sx={{ background: "#b9cc0340" }} />
            ),
        },
        {
            field: "criteria_type",
            headerName: "Criteria",
            width: 170,
            renderCell: (p) => (
                <Typography sx={{ fontSize: 12, color: "#000" }}>{p.value}</Typography>
            ),
        },
        {
            field: "is_premium",
            headerName: "Premium",
            width: 90,
            renderCell: (p) =>
                p.value ? (
                    <Chip size="small" label="Premium" sx={{ background: "#1d301020", color: "#000" }} />
                ) : (
                    <Chip size="small" label="Free" sx={{ background: "#c6ff0060", color: "#000" }} />
                ),
        },
        { field: "earn_count", headerName: "Earns", width: 75 },
        {
            field: "status",
            headerName: "Status",
            width: 100,
            renderCell: (p) => {
                const meta = STATUS_META[String(p.value)] || STATUS_META[0];
                return <Chip size="small" label={meta.label} sx={{ background: meta.bg, color: "#000" }} />;
            },
        },
        {
            field: "actions",
            headerName: "Actions",
            width: 170,
            sortable: false,
            renderCell: (p) => (
                <Box sx={{ display: "flex" }}>
                    <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEdit(p.row)}>
                            <EditIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Upload PNG art (square, 512px+)">
                        <IconButton size="small" onClick={() => pickArt(p.row)} disabled={uploadingId === p.row.id}>
                            {uploadingId === p.row.id
                                ? <CircularProgress size={16} />
                                : <UploadIcon sx={{ fontSize: 18 }} />}
                        </IconButton>
                    </Tooltip>
                    {p.row.status !== 1 && (
                        <Tooltip title={p.row.image ? "Go live" : "Upload art first"}>
                            <span>
                                <IconButton size="small" disabled={!p.row.image} onClick={() => setStatus(p.row, 1)}>
                                    <RocketLaunchIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                            </span>
                        </Tooltip>
                    )}
                    {p.row.status === 1 && (
                        <Tooltip title="Back to draft">
                            <IconButton size="small" onClick={() => setStatus(p.row, 0)}>
                                <UnpublishedIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Tooltip>
                    )}
                    {p.row.status !== -1 && (
                        <Tooltip title="Archive">
                            <IconButton size="small" onClick={() => setStatus(p.row, -1)}>
                                <ArchiveIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            ),
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [uploadingId]);

    return (
        <Box sx={{ width: "100%", height: "100vh", background: "#B9CC0310", overflow: "hidden" }}>
            <input
                ref={fileInputRef}
                type="file"
                accept="image/png"
                hidden
                onChange={onArtSelected}
            />
            <Box sx={{ width: "100%", p: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography sx={{ fontSize: { xs: "18px", md: "28px" }, fontWeight: 300, color: "#000" }}>
                    Badge Manager
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={openAdd}
                    sx={{ background: "#c6ff00", color: "#000", fontWeight: 600, "&:hover": { background: "#b5ee00" } }}
                >
                    Add Badge
                </Button>
            </Box>

            <Box sx={{ width: "100%", height: "calc(100vh - 90px)", p: 2 }}>
                <TableContainer
                    columns={columns}
                    rows={badges}
                    loading={loading}
                    page={page}
                    handlePageChange={setPage}
                    totalData={badges.length}
                    pageSize={pageSize}
                />
            </Box>

            <Dialog open={dialogOpen} onClose={saving ? undefined : () => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{form.id ? "Edit Badge" : "Add Badge"}</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth margin="normal" label="Name"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                    />
                    <TextField
                        fullWidth margin="normal" label="Code"
                        helperText="Stable slug: lowercase letters, digits, underscores (e.g. fall_explorer_2026)"
                        value={form.code}
                        disabled={!!form.id}
                        onChange={(e) => setForm({ ...form, code: e.target.value })}
                    />
                    <TextField
                        fullWidth margin="normal" multiline rows={2} label="Description"
                        helperText="User-facing 'how to earn' copy shown in the trophy case"
                        value={form.description}
                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                    />
                    <FormControl fullWidth margin="normal">
                        <InputLabel>Category</InputLabel>
                        <Select
                            value={form.category}
                            label="Category"
                            onChange={(e) => setForm({ ...form, category: e.target.value })}
                        >
                            {(categories.length ? categories : ["milestone"]).map((c) => (
                                <MenuItem key={c} value={c}>{c.replace("_", " ")}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth margin="normal">
                        <InputLabel>Criteria type</InputLabel>
                        <Select
                            value={form.criteria_type}
                            label="Criteria type"
                            onChange={(e) =>
                                setForm({ ...form, criteria_type: e.target.value, criteria_params: {} })
                            }
                        >
                            {Object.keys(criteriaTypes).map((t) => (
                                <MenuItem key={t} value={t}>{t}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {Object.entries(schema).map(([key, spec]) => paramField(key, spec))}
                    <TextField
                        fullWidth margin="normal" type="number" label="Sort order"
                        value={form.sort_order}
                        onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
                    />
                    <TextField
                        fullWidth margin="normal" type="number" label="XP reward (reserved, unused in v1)"
                        value={form.xp_reward}
                        onChange={(e) => setForm({ ...form, xp_reward: e.target.value })}
                    />
                    <TextField
                        fullWidth margin="normal" type="date" label="Available from"
                        InputLabelProps={{ shrink: true }}
                        helperText="Lifecycle window: WHEN the badge is earnable (blank = always). For seasonal badges also set a date_window criteria — that controls WHICH activity dates qualify."
                        value={form.available_from}
                        onChange={(e) => setForm({ ...form, available_from: e.target.value })}
                    />
                    <TextField
                        fullWidth margin="normal" type="date" label="Available until"
                        InputLabelProps={{ shrink: true }}
                        value={form.available_until}
                        onChange={(e) => setForm({ ...form, available_until: e.target.value })}
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={form.is_premium}
                                onChange={(e) => setForm({ ...form, is_premium: e.target.checked })}
                            />
                        }
                        label="Premium badge (free users see it as Ready to Claim)"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
                    <Button
                        onClick={saveBadge}
                        variant="contained"
                        disabled={saving}
                        sx={{ background: "#c6ff00", color: "#000", fontWeight: 600, "&:hover": { background: "#b5ee00" } }}
                    >
                        {saving ? "Saving…" : form.id ? "Update" : "Create draft"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default BadgeManager;
